// Chunked-cookie session storage on Domain=.seabearing.com — ONE sign-in across seabearing.com,
// app.seabearing.com and data.seabearing.com. Every surface creates its supabase client with this
// adapter and the SAME storageKey ('seabearing-auth'), so the session travels in cookies every
// subdomain can read, instead of per-origin localStorage. The landing site carries a plain-JS twin
// (landing/auth.js — no build step there); keep the cookie format in sync with it.
//
// Format: the session JSON is base64url-encoded and split across cookies `sb-<key>.0`, `.1`, …
// (~3 KB each — browsers cap one cookie at ~4 KB including attributes). On localhost/dev hosts the
// Domain attribute is omitted (host-only cookie) so nothing breaks off-domain.
//
// `mirror: true` additionally persists to this origin's localStorage and falls back to it when the
// cookie is missing, healing the cookie on read. That matters because Safari caps JS-set cookies at
// 7 days — a boat offline for a month must still restore its session (the app's offline-first
// invariant). Online-only surfaces pass mirror: false and live off the cookie alone.

export interface SharedStorageOptions {
  /** Keep a localStorage copy on this origin and restore from it when the cookie is gone. */
  mirror: boolean;
  /** Pre-SSO localStorage keys to migrate a session from on first read. */
  legacyKeys?: string[];
}

interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const CHUNK = 3000;
const COOKIE_LIFE_S = 34560000; // 400 days — the Chrome cap; Safari clamps JS cookies to 7 days

function cookieName(key: string): string {
  return 'sb-' + key.replace(/[^a-zA-Z0-9._-]/g, '-');
}

function cookieAttrs(remove: boolean): string {
  let attrs = '; Path=/; SameSite=Lax';
  const h = window.location.hostname;
  if (h === 'seabearing.com' || h.endsWith('.seabearing.com')) attrs += '; Domain=.seabearing.com';
  if (window.location.protocol === 'https:') attrs += '; Secure';
  return `${attrs}; Max-Age=${remove ? 0 : COOKIE_LIFE_S}`;
}

function readCookie(name: string): string | null {
  for (const part of document.cookie.split('; ')) {
    const eq = part.indexOf('=');
    if (part.slice(0, eq) === name) return part.slice(eq + 1);
  }
  return null;
}

// base64url with unicode safety (session JSON is ASCII in practice, but display names may not be)
function b64encode(s: string): string {
  return btoa(unescape(encodeURIComponent(s))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function b64decode(s: string): string | null {
  try {
    return decodeURIComponent(escape(atob(s.replace(/-/g, '+').replace(/_/g, '/'))));
  } catch {
    return null;
  }
}

function readChunks(base: string): string | null {
  let out = '';
  for (let i = 0; ; i++) {
    const c = readCookie(`${base}.${i}`);
    if (c === null) break;
    out += c;
  }
  return out || null;
}

function clearChunks(base: string, from: number): void {
  for (let i = from; ; i++) {
    if (readCookie(`${base}.${i}`) === null) break;
    document.cookie = `${base}.${i}=${cookieAttrs(true)}`;
  }
}

// localStorage can throw (private mode / quota); a session that only lives in memory for this
// page load is the graceful floor — same policy as the previous per-origin adapter.
const memory = new Map<string, string>();
function lsGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
}
function lsSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    memory.set(key, value);
  }
}
function lsRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
  memory.delete(key);
}

export function createSharedStorage(opts: SharedStorageOptions): StorageAdapter {
  const legacy = opts.legacyKeys ?? [];
  const adapter: StorageAdapter = {
    getItem(key: string): string | null {
      const raw = readChunks(cookieName(key));
      if (raw !== null) return b64decode(raw);
      // cookie gone (first visit here, or Safari expired it) → mirror, then pre-SSO legacy keys;
      // a hit re-writes the cookie so the other subdomains see this session again.
      let fallback = opts.mirror ? lsGet(key) : null;
      for (let i = 0; fallback === null && i < legacy.length; i++) fallback = lsGet(legacy[i]);
      if (fallback !== null) adapter.setItem(key, fallback);
      return fallback;
    },
    setItem(key: string, value: string): void {
      const base = cookieName(key);
      const enc = b64encode(value);
      let n = 0;
      for (; n * CHUNK < enc.length; n++) {
        document.cookie = `${base}.${n}=${enc.slice(n * CHUNK, (n + 1) * CHUNK)}${cookieAttrs(false)}`;
      }
      clearChunks(base, n);
      if (opts.mirror) lsSet(key, value);
    },
    removeItem(key: string): void {
      clearChunks(cookieName(key), 0);
      lsRemove(key);
      for (const k of legacy) lsRemove(k);
    },
  };
  return adapter;
}
